import React from 'react';
import { motion } from 'framer-motion';
import { SubscribeForm } from './SubscribeForm';
import { Button } from './ui/Button';
import { MessageSquare } from 'lucide-react';
interface HeroSectionProps {
  onOpenChat: () => void;
}
export function HeroSection({
  onOpenChat
}: HeroSectionProps) {
  return <section className="relative min-h-screen flex items-center pt-20 overflow-hidden bg-brand-cream" id="subscribe-section" data-id="element-130">
      {/* Background Decorative Elements - Blueprint Grid */}
      <div className="absolute inset-0 z-0 opacity-[0.03] pointer-events-none" style={{
      backgroundImage: 'linear-gradient(#2C2468 1px, transparent 1px), linear-gradient(90deg, #2C2468 1px, transparent 1px)',
      backgroundSize: '40px 40px'
    }} data-id="element-131"></div>

      {/* Abstract Geometric Shapes */}
      <div className="absolute top-1/4 right-0 w-1/3 h-1/2 border border-brand-navy/10 rounded-l-full z-0 pointer-events-none" data-id="element-132" />
      <div className="absolute bottom-0 left-1/4 w-1/4 h-1/3 border-t border-r border-brand-navy/10 rounded-tr-3xl z-0 pointer-events-none" data-id="element-133" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full py-12 lg:py-0" data-id="element-134">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center" data-id="element-135">
          {/* Content Column */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          ease: 'easeOut'
        }} className="max-w-2xl" data-id="element-136">
            <div className="flex items-center gap-4 mb-8" data-id="element-137">
              <img src="https://cdn.magicpatterns.com/uploads/p2FM8XEfS2zrSHemfzbCaz/The_Productivity_Blueprint_Logo-modified.png" alt="The Productivity Blueprint" className="h-16 md:h-20 w-auto" data-id="element-138" />
              <div className="inline-block px-3 py-1 bg-brand-navy/5 text-brand-navy text-xs font-semibold tracking-wider uppercase rounded-full" data-id="element-139">
                Weekly Productivity Insights
              </div>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-brand-navy leading-tight mb-6" data-id="element-140">
              Master Your Focus. <br data-id="element-141" />
              <span className="text-brand-gold relative" data-id="element-142">
                Multiply Your Impact.
                <svg className="absolute w-full h-3 -bottom-1 left-0 text-brand-gold/20" viewBox="0 0 100 10" preserveAspectRatio="none" data-id="element-143">
                  <path d="M0 5 Q 50 10 100 5" stroke="currentColor" strokeWidth="2" fill="none" data-id="element-144" />
                </svg>
              </span>
            </h1>

            <p className="text-lg md:text-xl text-brand-slate mb-8 leading-relaxed" data-id="element-145">
              Actionable insights on the science of productivity — the "why"
              behind the "how." Get the blueprint delivered every Sunday.
            </p>

            <div className="mb-8" data-id="element-146">
              <SubscribeForm variant="hero" data-id="element-147" />
            </div>

            <div className="flex items-center gap-4 text-sm text-brand-slate/80" data-id="element-148">
              <Button variant="ghost" className="pl-0 hover:bg-transparent hover:text-brand-gold group" onClick={onOpenChat} data-id="element-149">
                <span className="flex items-center gap-2" data-id="element-150">
                  <span className="p-2 bg-brand-gold/10 rounded-full text-brand-gold group-hover:bg-brand-gold group-hover:text-white transition-colors" data-id="element-151">
                    <MessageSquare size={18} data-id="element-152" />
                  </span>
                  Chat with your AI Productivity Guide →
                </span>
              </Button>
            </div>
          </motion.div>

          {/* Visual Column */}
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.8,
          delay: 0.2,
          ease: 'easeOut'
        }} className="hidden lg:block relative" data-id="element-153">
            <div className="relative z-10 bg-white p-8 rounded-xl shadow-2xl border border-gray-100 transform rotate-2 hover:rotate-0 transition-transform duration-500" data-id="element-154">
              <div className="absolute -top-4 -left-4 w-20 h-20 bg-brand-gold/20 rounded-full blur-xl" data-id="element-155"></div>
              <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-brand-navy/10 rounded-full blur-xl" data-id="element-156"></div>

              <div className="space-y-5 relative" data-id="element-157">
                {/* Newsletter Header */}
                <div className="flex items-center gap-3 pb-4 border-b border-gray-100" data-id="element-158">
                  <img src="https://cdn.magicpatterns.com/uploads/p2FM8XEfS2zrSHemfzbCaz/The_Productivity_Blueprint_Logo-modified.png" alt="The Productivity Blueprint" className="h-10 w-auto" data-id="element-159" />
                  <div data-id="element-160">
                    <p className="font-serif font-bold text-brand-navy text-sm leading-tight" data-id="element-161">
                      The Productivity Blueprint
                    </p>
                    <p className="text-[11px] text-gray-400" data-id="element-162">Every Sunday</p>
                  </div>
                </div>

                {/* Article Title */}
                <div data-id="element-163">
                  <h3 className="font-serif font-bold text-brand-navy text-xl leading-snug mb-1" data-id="element-164">
                    How to focus for 12 hours (without burning out)
                  </h3>
                  <p className="text-xs text-gray-400" data-id="element-165">November 06, 2025</p>
                </div>

                {/* Intro Text */}
                <p className="text-sm text-brand-slate leading-relaxed" data-id="element-166">
                  Welcome to The Productivity Blueprint newsletter ✏️
                </p>
                <p className="text-sm text-brand-slate leading-relaxed" data-id="element-167">
                  Feeling like the day slips by before you even start? This
                  week's issue digs into a method for locking in 12 solid hours
                  of meaningful focus—without burning out. Let's dive in.
                </p>

                {/* Quote Block */}
                <div className="bg-gray-50 rounded-lg p-4 border-l-3 border-l-brand-gold" data-id="element-168">
                  <p className="text-sm text-brand-slate italic leading-relaxed" data-id="element-169">
                    "You can already focus for 12 hours a day. When you're
                    playing a video game or scrolling on your phone, it's
                    automatic."
                  </p>
                  <p className="text-xs text-brand-gold font-medium mt-2" data-id="element-170">
                    — Dan Koe
                  </p>
                </div>

                {/* Article of the Week teaser */}
                <div className="pt-3 border-t border-gray-100" data-id="element-171">
                  <p className="text-[10px] font-bold text-brand-navy uppercase tracking-widest text-center mb-2" data-id="element-172">
                    Article of the Week
                  </p>
                  <p className="text-sm font-serif font-semibold text-brand-navy leading-snug" data-id="element-173">
                    How To Focus For 12 Hours A Day On Your Purpose
                  </p>
                  <p className="text-xs text-gray-400 mt-1 line-clamp-2" data-id="element-174">
                    Dan Koe explains that long, deep focus isn't about willpower
                    but about clarity and timing...
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}